import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { Card } from 'primereact/card';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { AuthContext } from '../../Header/auth - context';
import './SnimciPoziva.css';

/* Chat → Snimci poziva (21.10.2026).
   Bio je pod Administracija → Korisnik, pa je prebačen ovamo: poziv se pokreće
   iz Chata, pa se i snimak traži tu — dugme je u zaglavlju liste razgovora.
   Snimci razgovora u kojima je korisnik učestvovao. Zvuk se ne poslužuje javno —
   učitava se uz token (/chat/snimakPoziva/:id/audio) i pušta iz memorije.
   Briše onaj ko je snimanje pokrenuo ili čiji je računar snimao. */
const trajanjeTekst = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, '0')}`;

/* `bezOkvira` — kad se prikazuje u modalu Chata, koji vec ima svoje zaglavlje i
   okvir. Bez toga bi se videla dva naslova i kartica u kartici. */
const SnimciPoziva = ({ bezOkvira = false }) => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const toast = useRef(null);
    const [snimci, setSnimci] = useState(null);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [svira, setSvira] = useState(null);          // { id, url }
    const [ucitava, setUcitava] = useState(null);      // id koji se učitava
    const backend = process.env.REACT_APP_BACKEND_URL;

    const ucitaj = useCallback(() => {
        fetch(backend + '/chat/snimciPoziva', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: '{}',
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((d) => setSnimci(Array.isArray(d) ? d : []))
            .catch(() => {
                setSnimci([]);
                toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('snimci.greskaUcitavanja'), life: 4000 });
            });
    }, [backend, auth.token, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);
    useEffect(() => () => { if (svira?.url) URL.revokeObjectURL(svira.url); }, [svira]);

    const blobSnimka = async (id) => {
        const r = await fetch(`${backend}/chat/snimakPoziva/${id}/audio`, { headers: { Authorization: 'Bearer ' + auth.token } });
        if (!r.ok) throw r;
        return r.blob();
    };

    const slusaj = async (red) => {
        if (svira?.id === red.id) { setSvira(null); return; }
        setUcitava(red.id);
        try {
            const blob = await blobSnimka(red.id);
            setSvira({ id: red.id, url: URL.createObjectURL(blob) });
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('snimci.greskaSlusanja'), life: 4000 });
        } finally {
            setUcitava(null);
        }
    };

    const preuzmi = async (red) => {
        try {
            const blob = await blobSnimka(red.id);
            const a = document.createElement('a');
            const url = URL.createObjectURL(blob);
            a.href = url;
            a.download = `Poziv ${red.Sagovornik || ''} ${String(red.Datum || '').replace(/[:.]/g, '-')}.${/ogg/.test(red.mime) ? 'ogg' : 'webm'}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 5000);
        } catch (e) {
            toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('snimci.greskaSlusanja'), life: 4000 });
        }
    };

    const obrisi = (red) => confirmDialog({
        header: t('snimci.obrisiNaslov'),
        message: t('snimci.obrisiPitanje', { ko: red.Sagovornik || '', datum: red.Datum }),
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: t('common.da'),
        rejectLabel: t('common.ne'),
        accept: () => fetch(backend + '/chat/snimakPozivaObrisi', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
            body: JSON.stringify({ id: red.id }),
        })
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((d) => {
                toast.current?.show({ severity: 'success', summary: t('common.uspeh'), detail: d.message, life: 2500 });
                if (svira?.id === red.id) setSvira(null);
                ucitaj();
            })
            .catch(() => toast.current?.show({ severity: 'error', summary: t('common.greska'), detail: t('snimci.greskaBrisanja'), life: 4000 })),
    });

    const zaglavlje = (
        <div className="export-buttons fakture-stavke-header">
            {/* Dugme je DIREKTAN potomak `.export-buttons` — tooltip cilja
                `.export-buttons>button`, pa ugnjezdeno u div nije bilo pokriveno
                i natpis "Osveži" se nije video. Ista zamka kao kod XLS dugmeta. */}
            <Button type="button" icon="pi pi-refresh" severity="secondary" rounded
                    onClick={ucitaj} data-pr-tooltip={t('common.osvezi')} />
            <input type="search" onInput={(e) => setGlobalFilter(e.target.value)}
                placeholder={t('common.search')} className="p-inputtext p-component" />
        </div>
    );

    /* Sadrzaj je isti na oba mesta — menja se samo okvir oko njega. */
    const sadrzaj = (
        <>

            {svira && (
                <div className="snimci-plejer">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio src={svira.url} controls autoPlay />
                    <Button icon="pi pi-times" rounded text severity="secondary" onClick={() => setSvira(null)} aria-label={t('snimci.zaustavi')} />
                </div>
            )}

            <DataTable
                className="stavke-tabela"
                value={snimci || []}
                loading={snimci === null}
                header={zaglavlje}
                globalFilter={globalFilter}
                paginator rows={10}
                scrollable scrollHeight="55vh"
                resizableColumns columnResizeMode="expand"
                emptyMessage={t('snimci.nema')}
                rowClassName={(r) => (svira?.id === r.id ? { 'selected-row': true } : {})}
            >
                <Column header={t('snimci.slusaj')} style={{ minWidth: '120px' }}
                    body={(r) => (
                        <Button type="button" className="p-button-text p-button-sm"
                            icon={svira?.id === r.id ? 'pi pi-stop' : 'pi pi-play'}
                            label={svira?.id === r.id ? t('snimci.zaustavi') : t('snimci.slusaj')}
                            loading={ucitava === r.id} onClick={() => slusaj(r)} />
                    )} />
                <Column field="Datum" header={t('snimci.kolDatum')} sortable sortField="DateEntered" style={{ minWidth: '150px' }} />
                <Column field="Sagovornik" header={t('snimci.kolSagovornik')} sortable style={{ minWidth: '180px' }} body={(r) => r.Sagovornik || '—'} />
                <Column field="trajanjeSek" header={t('snimci.kolTrajanje')} sortable style={{ minWidth: '100px' }}
                    body={(r) => <span className="snimci-broj">{trajanjeTekst(r.trajanjeSek)}</span>} />
                <Column field="Pokrenuo" header={t('snimci.kolPokrenuo')} sortable style={{ minWidth: '160px' }} body={(r) => r.Pokrenuo || '—'} />
                <Column header={t('snimci.kolRadnje')} style={{ minWidth: '110px' }}
                    body={(r) => (
                        <div className="snimci-akcije">
                            <Button type="button" icon="pi pi-download" rounded text onClick={() => preuzmi(r)}
                                tooltip={t('snimci.preuzmi')} tooltipOptions={{ position: 'top' }} aria-label={t('snimci.preuzmi')} />
                            {!!r.mozeBrisati && (
                                <Button type="button" icon="pi pi-trash" rounded text severity="danger" onClick={() => obrisi(r)}
                                    tooltip={t('snimci.obrisiNaslov')} tooltipOptions={{ position: 'top' }} aria-label={t('snimci.obrisiNaslov')} />
                            )}
                        </div>
                    )} />
            </DataTable>
        </>
    );

    if (bezOkvira) {
        return (
            <div className="snimci-poziva snimci-u-modalu">
                <Toast ref={toast} />
                <ConfirmDialog />
                <Tooltip target=".export-buttons>button" position="bottom" />
                <p className="snimci-uvod">{t('snimci.uvod')}</p>
                {sadrzaj}
            </div>
        );
    }

    /* Omotac je isti kao kod ostalih jezicaka pod Korisnik (Promena lozinke):
       `col-12` pa `Card` — bez toga ekran stoji go uz ostale, bez okvira. */
    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />
            <Tooltip target=".export-buttons>button" position="bottom" />
            <div className="snimci-poziva">
                <Card>
                    <div className="snimci-zaglavlje">
                        <span className="snimci-ikona"><i className="pi pi-microphone" /></span>
                        <div>
                            <h3>{t('snimci.tab')}</h3>
                            <p className="snimci-uvod">{t('snimci.uvod')}</p>
                        </div>
                    </div>
                    {sadrzaj}
                </Card>
            </div>
        </div>
    );
};

export default SnimciPoziva;
